import { ok, err, type Result } from '../core/result.js';

export interface ClipboardError {
  code: 'UNAVAILABLE' | 'DENIED' | 'WRITE_ERROR';
  message: string;
}

export async function copyText(text: string): Promise<Result<void, ClipboardError>> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return ok(undefined);
    } catch (e) {
      // fall through to execCommand fallback below
      void e;
    }
  }
  if (typeof document === 'undefined') {
    return err({ code: 'UNAVAILABLE', message: 'no clipboard or document available' });
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success ? ok(undefined) : err({ code: 'WRITE_ERROR', message: 'execCommand("copy") returned false' });
  } catch (e) {
    return err({ code: 'WRITE_ERROR', message: (e as Error).message });
  }
}

export async function copyHtml(html: string, plaintextFallback?: string): Promise<Result<void, ClipboardError>> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      const items: Record<string, Blob> = { 'text/html': new Blob([html], { type: 'text/html' }) };
      if (plaintextFallback !== undefined) items['text/plain'] = new Blob([plaintextFallback], { type: 'text/plain' });
      await navigator.clipboard.write([new ClipboardItem(items)]);
      return ok(undefined);
    } catch (e) {
      return err({ code: 'WRITE_ERROR', message: (e as Error).message });
    }
  }
  return copyText(plaintextFallback ?? html);
}

export async function copyImage(blob: Blob): Promise<Result<void, ClipboardError>> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    return err({ code: 'UNAVAILABLE', message: 'clipboard image API unavailable' });
  }
  try {
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    return ok(undefined);
  } catch (e) {
    return err({ code: 'WRITE_ERROR', message: (e as Error).message });
  }
}
